import { useMutation, type FetchResult } from "@apollo/client";

import type {
  AddServiceInput,
  AddServiceMutation,
  AddServiceMutationVariables,
  GetServicesByNodeQuery,
} from "./../../../API";
import { ADD_SERVICE } from "./../../../common/operations/mutations";
import { useSyncShadow } from "./useSyncShadow";
import { GET_SERVICES_BY_NODE } from "../../../common/operations/queries";
import { useTimeZoneFromLocationId } from "../../../common/hooks/useTimeZoneFromLocationId";

interface CreateServiceInterface {
  createService: (
    input: Omit<AddServiceInput, "timeZone">
  ) => Promise<FetchResult<AddServiceMutation>>;
  data?: AddServiceMutation | null;
  loading: boolean;
}

export const useCreateService = (): CreateServiceInterface => {
  const { syncShadowsForNode } = useSyncShadow();

  const [saveService, { data, loading }] = useMutation<
    AddServiceMutation,
    AddServiceMutationVariables
  >(ADD_SERVICE);

  const createService = async (
    input: Omit<AddServiceInput, "timeZone">
  ): Promise<FetchResult<AddServiceMutation>> => {
    const timeZone = useTimeZoneFromLocationId(input.locationId);

    return await saveService({
      variables: {
        input: {
          ...input,
          timeZone,
        },
      },
      update(cache, response): void {
        const newService = response.data?.addService;

        if (!newService) return;

        const queryData = cache.readQuery<GetServicesByNodeQuery>({
          query: GET_SERVICES_BY_NODE,
          variables: { nodeId: newService.nodeId },
        });

        if (queryData) {
          cache.writeQuery({
            query: GET_SERVICES_BY_NODE,
            variables: { nodeId: newService.nodeId },
            data: {
              getServicesByNode: {
                ...queryData.getServicesByNode,
                items: [...queryData.getServicesByNode.items, newService],
              },
            },
          });
        }

        syncShadowsForNode(input.nodeId);
      },
      refetchQueries: ["GetDeviceById"],
    });
  };

  return { createService, data, loading };
};
